<template>
  <z-card width="auto" class="pa-4" style="max-width: 800px">
    <template #content>
      <div class="content-menu-container">
        <z-card width="auto" height="fit-content" shadow class="px-1 py-2">
          <template #content>
            <v-list class="pa-0" nav dense style="background-color: unset">
              <v-list-item-group
                :color="color ? color : 'var(--accent)'"
                mandatory
                :value="internalInterval"
                @change="$emit('internalIntervalChange', $event)"
              >
                <template v-for="(item, index) in filteredItems">
                  <z-tooltip
                    :key="`tp-${index}`"
                    :disabled="item.value !== 'now'"
                  >
                    <template #activator="{on, attrs}">
                      <v-list-item
                        v-if="!item.divider"
                        :value="item.value"
                        v-bind="attrs"
                        v-on="on"
                      >
                        <template #default="{active}">
                          <v-list-item-content>{{
                            item.text
                          }}</v-list-item-content>

                          <v-list-item-action
                            v-if="active && item.value !== 'compare'"
                          >
                            <z-icon size="15"> $check_rounded </z-icon>
                          </v-list-item-action>
                        </template>
                      </v-list-item>
                    </template>

                    {{ $t('data_from_last_hour') }}
                  </z-tooltip>

                  <v-divider
                    v-if="item.divider"
                    :key="`divider-${index}`"
                    class="my-1"
                  ></v-divider>
                </template>
              </v-list-item-group>
            </v-list>
          </template>
        </z-card>

        <div key="picker" class="picker-container">
          <div class="picker-info">
            <div class="date-title">{{ currentSelectedText }}:</div>

            <div class="description">
              {{ localizedDateText }}
            </div>
          </div>

          <v-date-picker
            v-if="internalInterval !== 'quarter' && internalInterval !== 'year'"
            no-title
            show-adjacent-months
            :color="color ? color : 'var(--accent)'"
            next-icon="$keyboard_arrow_right_rounded"
            prev-icon="$keyboard_arrow_left_rounded"
            first-day-of-week="1"
            :disabled="shouldDisabledCalendar"
            :type="pickerType"
            :class="{
              'picker-week': internalInterval === 'week',
            }"
            :range="shouldPickerAcceptRange"
            :readonly="shouldPickerBeReadonly"
            :show-current="false"
            :min="minDate"
            :max="maxDate"
            :value="computedInternalValue"
            :picker-date="pickerDate"
            @update:picker-date="$emit('update:picker-date', $event)"
            @input="$emit('inputDate', $event)"
            @change="$emit('changeDate', $event)"
          ></v-date-picker>

          <quarter-filter
            v-if="internalInterval === 'quarter'"
            key="quarter"
            :value="selectedQuarter"
            :max-date="maxDate"
            :min-date="minDate"
            :color="color"
            @input="$emit('update:selectedQuarter', $event)"
          >
          </quarter-filter>

          <div
            v-if="internalInterval === 'year'"
            key="year"
            class="year-container"
          >
            <v-date-picker-years
              :color="color ? color : 'var(--accent)'"
              :style="activeYearStyle"
              :value="yearInternalValue"
              :min="minDate"
              :max="maxDate"
              @input="$emit('changeDate', $event)"
            ></v-date-picker-years>
          </div>
        </div>
      </div>

      <div class="d-flex mt-2 justify-space-between align-end">
        <slot name="extra-content">
          <v-spacer></v-spacer>
        </slot>

        <div style="display: flex; gap: 16px">
          <z-btn
            v-if="!isCardType"
            :rounded="rounded"
            :small="small"
            :color="color"
            :text="cancelButtonText || $t('cancel')"
            @click="$emit('cancel', $event)"
          ></z-btn>

          <z-btn
            primary
            :rounded="rounded"
            :small="small"
            :color="color"
            :dark="!shouldDisabledConfirm"
            :disabled="shouldDisabledConfirm"
            :text="confirmButtonText || $t('confirm')"
            @click="$emit('confirm')"
          ></z-btn>
        </div>
      </div>
    </template>
  </z-card>
</template>

<script>
import QuarterFilter from './QuarterFilter.vue'
export default {
  components: {
    QuarterFilter,
  },
  props: {
    internalInterval: {
      type: String,
      default: '',
    },
    filteredItems: {
      type: Array,
      default: () => [],
    },
    color: {
      type: String,
      default: '',
    },
    currentSelectedText: {
      type: String,
      default: '',
    },
    localizedDateText: {
      type: String,
      default: '',
    },
    shouldDisabledCalendar: {
      type: Boolean,
      default: false,
    },
    pickerType: {
      type: String,
      default: '',
    },
    shouldPickerAcceptRange: {
      type: Boolean,
      default: false,
    },
    shouldPickerBeReadonly: {
      type: Boolean,
      default: false,
    },
    minDate: {
      type: String,
      default: '',
    },
    maxDate: {
      type: String,
      default: '',
    },
    computedInternalValue: {
      type: [String, Array],
      default: '',
    },
    pickerDate: {
      type: String,
      default: '',
    },
    selectedQuarter: {
      type: String,
      default: '',
    },
    activeYearStyle: {
      type: Object,
      default: () => ({}),
    },
    yearInternalValue: {
      type: [String, Number],
      default: '',
    },
    rounded: {
      type: Boolean,
      default: false,
    },
    small: {
      type: Boolean,
      default: false,
    },
    shouldDisabledConfirm: {
      type: Boolean,
      default: false,
    },
    confirmButtonText: {
      type: String,
      default: '',
    },
    isCardType: {
      type: Boolean,
      default: false,
    },
    cancelButtonText: {
      type: String,
      default: '',
    },
  },
}
</script>

<style lang="scss" scoped>
.content-menu-container {
  display: grid;
  grid-template-columns: 200px 290px;
  column-gap: 16px;

  :deep(.v-date-picker-header) {
    padding: 4px 0px;
  }

  :deep(.v-date-picker-table) {
    padding: 0px;

    .v-btn.v-btn--active {
      color: #fff !important;
    }
  }

  .picker-container {
    display: grid;
    gap: 5px;
    grid-template-columns: 1fr;
    grid-template-rows: auto 1fr;
    font-size: 15px;
    line-height: 18px;

    :deep(table) {
      border-spacing: 0px;
    }

    .picker-week {
      :deep(.v-date-picker-table--date tbody tr:hover) {
        background-color: #cecece;
      }
    }

    :deep(.v-date-picker-table--month button) {
      border-radius: 20px;
    }

    .date-title {
      font-weight: 400;
    }

    .description {
      font-weight: 700;
    }
  }

  .year-container {
    margin-top: 8px;

    ul {
      height: 278px;
      max-height: 278px;
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 5px;

      :deep(li) {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 8px 32px !important;
        border-radius: 20px !important;
      }

      :deep(.active) {
        color: var(--white) !important;
        background-color: var(--v-picker-year-active-background) !important;
        font-size: inherit !important;
      }
    }
  }
}

:deep(.v-list-item) {
  min-height: 25px !important;
  height: 25px !important;
}

:deep(td > .v-btn--active::before) {
  opacity: 0 !important;
}
</style>
